import { useEffect, useState } from "react"
import type { EUSignLibraryAdapter } from "iit/browser"
import type { OtkBlank, OtkDocumentDetail, SignedOtkDocumentPayload } from "shared"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { DocumentDetailField } from "@/components/DocumentDetailField"
import { ScenarioNotice } from "@/components/ScenarioNotice"
import { KepKeyReader } from "@/components/kep/KepKeyReader"
import {
  OtkDocumentValidationError,
  serializeOtkDocumentForSigning,
} from "@/lib/api/documents"
import { getOtkBlanksPage } from "@/lib/api/warehouse"

export interface SignedDocumentInfo {
  electronicLabel: string
  blankLabel?: string
  keyLabel: string
}

type BlankOption = { id: string; series: string; number: string; label: string }

// `"{series} {number}"` — document-editor.md's own label format for both
// pickers below. `series`/`number` are kept alongside the label since
// `pidpysannya-dokumenta-otk.md` step 6 signs the exact series+number, not
// this dropdown's own `id`.
function toBlankOption(blank: OtkBlank): BlankOption {
  return {
    id: blank.id,
    series: blank.series,
    number: blank.number,
    label: `${blank.series} ${blank.number}`,
  }
}

// The real `GET /otk/blanks` (`lib/api/warehouse.ts`) has no пункт-scoping
// param at all — `OtkBlank` carries no пункт field (see `lib/warehouse.ts`'s
// own gap note) — so unlike document-editor.md's `b.punkt === punkt`
// point-isolation, this offers every `IN_STOCK` unit of the subject, not
// just the signing пункт's own. `pageSize: 50` (the endpoint's documented
// max) rather than the default 25, so a dropdown isn't silently truncated
// for a subject with more stock than that — no further pagination for a
// picker this size.
const blanksQuery = { pageSize: 50 as const, status: "IN_STOCK" as const }

/**
 * document-editor.md's merged sign flow (formerly the standalone
 * `SignDialog`/DE10, deleted as its own `Dialog` — this form now renders
 * inside `DocumentPreviewDialog`'s left column instead). `electronicUnits`/
 * `paperBlanks` are the subject's real спецпродукція warehouse stock,
 * fetched here directly via `getOtkBlanksPage` rather than through
 * `useWarehouseStore` (the `/warehouse` page's own store): that store keys
 * its `available` bucket by a single `fetchedType`, so fetching both blank
 * types through it would have each overwrite the other's cached list —
 * this form needs both electronic and physical stock at once.
 */
export function SignDialogForm({
  documentNumber,
  isNonconformityAct,
  needsBlank,
  buildPayload,
  onSubmit,
  onSigned,
}: {
  documentNumber: string
  isNonconformityAct: boolean
  needsBlank: boolean
  /**
   * Builds the exact JSON body to sign for the chosen electronic/blank units
   * (`pidpysannya-dokumenta-otk.md` step 6 — series+number are part of the
   * signed payload, not just a submit-time parameter).
   */
  buildPayload: (selection: {
    electronicSeries: string
    electronicNumber: string
    blankSeries?: string
    blankNumber?: string
  }) => Promise<SignedOtkDocumentPayload>
  /**
   * Sends the exact signed JSON bytes as the request body, with the detached
   * signature in `X-KEP-Signature` (`submitSignedOtkDocument`).
   */
  onSubmit: (
    signedPayload: string,
    kepSignature: string
  ) => Promise<
    { ok: true; document: OtkDocumentDetail } | { ok: false; message: string }
  >
  onSigned: (document: OtkDocumentDetail, info: SignedDocumentInfo) => void
}) {
  const [electronicUnits, setElectronicUnits] = useState<BlankOption[]>([])
  const [paperBlanks, setPaperBlanks] = useState<BlankOption[]>([])
  const [isLoadingElectronic, setIsLoadingElectronic] = useState(true)
  const [isLoadingPaper, setIsLoadingPaper] = useState(true)
  // `electronicId` is seeded with the first candidate once the list arrives —
  // a convenience this package keeps; document-editor.md no longer documents
  // a default either way for this picker, only that selecting reserves
  // nothing. `blankId` deliberately never gets a default (P2C: "explicit
  // selection only") even after `paperBlanks` loads.
  const [electronicId, setElectronicId] = useState("")
  const [blankId, setBlankId] = useState("")
  const [pendingKey, setPendingKey] = useState<
    { adapter: EUSignLibraryAdapter; keyLabel: string } | null
  >(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  /** Set alongside `submitError` only for `OtkDocumentValidationError` — the specific fields that failed `signedOtkDocumentPayloadSchema`, rendered as a list under the generic message. */
  const [invalidFields, setInvalidFields] = useState<string[]>([])

  // This component is only ever mounted while `DocumentPreviewDialog`'s
  // sign card is open (rendered under `{open && <SignDialogForm .../>}`),
  // so a plain mount effect already matches document-editor.md's "local
  // state resets on every open, not on every mount" rule — no extra `open`
  // dependency needed here, unlike that dialog's own print-profile fetch.
  useEffect(() => {
    let cancelled = false
    getOtkBlanksPage({ ...blanksQuery, type: "ELECTRONIC_OTK_DOCUMENT" })
      .then((page) => {
        if (cancelled) return
        const units = page.data.map(toBlankOption)
        setElectronicUnits(units)
        setElectronicId(units[0]?.id ?? "")
      })
      .catch((error) => {
        console.error("Не вдалося завантажити електронні документи ОТК", error)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingElectronic(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    getOtkBlanksPage({ ...blanksQuery, type: "PROTOCOL_FORM" })
      .then((page) => {
        if (cancelled) return
        setPaperBlanks(page.data.map(toBlankOption))
      })
      .catch((error) => {
        console.error("Не вдалося завантажити бланки суворої звітності", error)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPaper(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const hasElectronic = electronicUnits.length > 0
  const hasPhysical = paperBlanks.length > 0
  const showBlankPicker = !isNonconformityAct && needsBlank
  const canSign = hasElectronic && (!showBlankPicker || Boolean(blankId))
  const selectedElectronic = electronicUnits.find(
    (unit) => unit.id === electronicId
  )
  const selectedBlank = paperBlanks.find((blank) => blank.id === blankId)

  // `pidpysannya-dokumenta-otk.md`'s "Підписання й відправлення — одна
  // атомарна операція": build the exact payload, sign it, and submit in one
  // sequence triggered by this dialog's own confirmation button — there is
  // no separate "send the already-signed document" step.
  async function handleConfirm() {
    if (!pendingKey || !selectedElectronic || isSubmitting) return
    setIsSubmitting(true)
    setSubmitError(null)
    setInvalidFields([])
    try {
      const payload = await buildPayload({
        electronicSeries: selectedElectronic.series,
        electronicNumber: selectedElectronic.number,
        ...(showBlankPicker && selectedBlank
          ? { blankSeries: selectedBlank.series, blankNumber: selectedBlank.number }
          : {}),
      })
      const signedPayload = serializeOtkDocumentForSigning(payload)
      const kepSignature = await pendingKey.adapter.sign(
        new TextEncoder().encode(signedPayload)
      )
      const result = await onSubmit(signedPayload, kepSignature)
      if (!result.ok) {
        setSubmitError(result.message)
        return
      }
      onSigned(result.document, {
        electronicLabel: selectedElectronic.label,
        ...(showBlankPicker && selectedBlank
          ? { blankLabel: selectedBlank.label }
          : {}),
        keyLabel: pendingKey.keyLabel,
      })
      setPendingKey(null)
    } catch (error) {
      if (error instanceof OtkDocumentValidationError) {
        setSubmitError(
          "Дані документа не пройшли перевірку. Перевірте заповнені поля і спробуйте ще раз."
        )
        setInvalidFields(error.fields)
      } else {
        setSubmitError(
          "Не вдалося підписати документ КЕП. Перевірте носій ключа і спробуйте ще раз."
        )
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Field>
        <FieldLabel htmlFor="sign-electronic">
          Електронний документ ОТК
        </FieldLabel>
        <Select onValueChange={setElectronicId} value={electronicId}>
          <SelectTrigger id="sign-electronic" length="full-size">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {electronicUnits.map((unit) => (
              <SelectItem key={unit.id} value={unit.id}>
                {unit.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldDescription>
          {isLoadingElectronic
            ? "Завантаження..."
            : hasElectronic
              ? "Серія і номер увійдуть до підписаного пакета. Якщо документ уже займе інший користувач, потрібно буде оновити список і підписати заново."
              : "На складі немає жодного вільного електронного документа — зверніться на склад спецпродукції."}
        </FieldDescription>
      </Field>

      {isNonconformityAct ? (
        <p className="text-sm text-muted-foreground">
          Акт невідповідності оформлюється лише в електронному вигляді —
          паперовий бланк суворої звітності для нього не потрібен.
        </p>
      ) : (
        needsBlank && (
          <Field>
            <FieldLabel htmlFor="sign-blank">
              Бланк суворої звітності
            </FieldLabel>
            <Select
              disabled={!hasPhysical}
              onValueChange={setBlankId}
              value={blankId}
            >
              <SelectTrigger id="sign-blank" length="full-size">
                <SelectValue placeholder="Оберіть бланк" />
              </SelectTrigger>
              <SelectContent>
                {paperBlanks.map((blank) => (
                  <SelectItem key={blank.id} value={blank.id}>
                    {blank.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldDescription>
              {isLoadingPaper
                ? "Завантаження..."
                : hasPhysical
                  ? "Буде надруковано на цьому бланку."
                  : "На складі немає жодного вільного паперового бланка — зверніться на склад спецпродукції."}
            </FieldDescription>
          </Field>
        )
      )}

      <KepKeyReader
        actionLabel="Підписати та відправити до ЄДРТЗ"
        actionSize="lg"
        disabled={!canSign}
        onRead={({ adapter, keyLabel }) => setPendingKey({ adapter, keyLabel })}
      />

      <AlertDialog
        onOpenChange={(open) => {
          if (!open && !isSubmitting) {
            setPendingKey(null)
            setSubmitError(null)
            setInvalidFields([])
          }
        }}
        open={pendingKey !== null}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Підписати та Відправити Документ до ЄДРТЗ
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="flex flex-col gap-3 text-left">
                <p>
                  До ЄДРТЗ буде відправлено всі заповнені дані, а також
                  наступна інформація:
                </p>
                <dl className="flex flex-col gap-2 rounded-lg border bg-muted/30 p-3">
                  <DocumentDetailField
                    label="Документ:"
                    value={documentNumber}
                  />
                  <DocumentDetailField
                    label="Електронний документ ОТК:"
                    value={selectedElectronic?.label ?? "—"}
                  />
                  {showBlankPicker && selectedBlank && (
                    <DocumentDetailField
                      label="Бланк суворої звітності:"
                      value={selectedBlank.label}
                    />
                  )}
                  <DocumentDetailField
                    label="Носій особистого ключа:"
                    value={pendingKey?.keyLabel ?? "—"}
                  />
                </dl>
                <p>
                  Відправлену інформацію не можна буде змінити або
                  доповнити.
                </p>
                {submitError && (
                  <ScenarioNotice tone="danger">
                    {submitError}
                    {invalidFields.length > 0 && (
                      <ul className="mt-1 list-disc pl-4">
                        {invalidFields.map((field) => (
                          <li key={field}>{field}</li>
                        ))}
                      </ul>
                    )}
                  </ScenarioNotice>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>Назад</AlertDialogCancel>
            <AlertDialogAction
              // document-editor.md gates this on the carrier *and* the
              // electronic unit. The carrier half is structural here — this
              // `AlertDialog` is only open while `pendingKey !== null` — but
              // the unit is not: `canSign` upstream only checks that the
              // candidate list is non-empty, so a list that loaded without
              // selecting anything would otherwise reach a confirm button
              // that silently does nothing.
              disabled={isSubmitting || !selectedElectronic}
              onClick={(event) => {
                event.preventDefault()
                void handleConfirm()
              }}
            >
              {isSubmitting ? "Підписання..." : "Підписати та Відправити до ЄДРТЗ"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
